/**
 * IB-style saldo (balance) and extrato (statement) for the BaaS console.
 * Uses MobileService `/v1/ib/console/*` → BaaS with the selected instance (`X-Instance-Id`).
 */

import {
  fetchAccountBalances,
  fetchAccountCurrencies,
  fetchAccountStatements,
  fetchBaasAccounts,
  mergeBalancesWithCurrencyOpenings,
  mergeCurrencyOpeningLists,
} from "./baasAccountsListApi";
import { fetchBaasTransactions } from "./baasTransactionsApi";

/**
 * Account balances via MobileService console proxy → BaaS
 * `GET /v1/accounts/{accountId}/balances`.
 */
export const fetchIbAccountBalance = async (instance, accountId) => {
  if (!instance || !accountId) return [];
  return fetchAccountBalances(instance, accountId);
};

/**
 * Currency openings (per-moeda banking) via console proxy → BaaS
 * `GET /v1/accounts/{accountId}/currencies`.
 */
export const fetchIbAccountCurrencies = async (instance, accountId) => {
  if (!instance || !accountId) return [];
  return fetchAccountCurrencies(instance, accountId);
};

/**
 * Balances + per-currency banking for account detail.
 * @param {object} instance
 * @param {string} accountId
 * @param {object|null} [fallbackBanking] Account-level banking when no openings exist.
 * @param {array} [listOpenings] Openings preserved from the accounts list (pre-dedupe merge).
 */
export const fetchIbAccountCurrencyViews = async (
  instance,
  accountId,
  fallbackBanking = null,
  listOpenings = [],
) => {
  if (!instance || !accountId) return [];

  const [balances, openings] = await Promise.all([
    fetchAccountBalances(instance, accountId),
    fetchAccountCurrencies(instance, accountId),
  ]);

  const mergedOpenings = mergeCurrencyOpeningLists(openings, listOpenings);

  return mergeBalancesWithCurrencyOpenings(balances, mergedOpenings, fallbackBanking);
};

/**
 * Account statements via MobileService console proxy → BaaS
 * `GET /v1/accounts/{accountId}/statements`.
 */
export const fetchIbAccountStatements = async (instance, accountId, query = {}) => {
  if (!instance || !accountId) return { items: [], total: 0, page: 1, page_size: 100 };
  // Normalize pageSize → page_size and paginate when fetch_all / pageSize > 100.
  return fetchAccountStatements(instance, accountId, query);
};

/**
 * Aggregated saldo across all accounts of the selected BaaS instance/environment.
 * Intentionally hydrates balances per account (console aggregate view only).
 */
export const fetchBaasSaldoSummary = async (instance) => {
  if (!instance) {
    return { accounts: [], totalsByCurrency: [], environment: null };
  }

  // Light list first, then one balance call per account for this console page only.
  const accounts = await fetchBaasAccounts(instance, { includeBalances: false }).catch(() => []);

  const withFreshBalances = await Promise.all(
    accounts.map(async (account) => {
      const balances = await fetchIbAccountBalance(instance, account.id);
      return {
        ...account,
        balances: balances.length ? balances : account.balances || [],
      };
    }),
  );

  const totalsMap = new Map();
  withFreshBalances.forEach((account) => {
    (account.balances || []).forEach((balance) => {
      const currency = String(balance.currency || "").toUpperCase();
      if (!currency) return;
      const current = totalsMap.get(currency) || { currency, available: 0, blocked: 0 };
      current.available += Number(balance.available || 0);
      current.blocked += Number(balance.blocked || 0);
      totalsMap.set(currency, current);
    });
  });

  return {
    accounts: withFreshBalances,
    totalsByCurrency: [...totalsMap.values()].sort((a, b) =>
      a.currency.localeCompare(b.currency),
    ),
    environment: instance.environment || null,
    instanceId: instance.baasInstanceId || instance.instanceId || null,
  };
};

/**
 * Instance-wide extrato (statements + recent Pix) for the selected environment.
 */
export const fetchBaasExtrato = async (instance, options = {}) => {
  if (!instance) return [];
  return fetchBaasTransactions(instance, options);
};
